
'use client';
import { JartinetHermandad, PapeletaDeSitio, UserProfile } from "@/lib/types";
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from "@/lib/utils";

interface PapeletaProps {
    hermandad: Partial<JartinetHermandad>;
    hermano: UserProfile | null;
    diaDeSalida: string;
    cargo: string;
    hora: string;
    fechaCreacion: Date;
    ornamentColor: string;
    titleColor: string;
    selloColor?: string;
    selloOpacity?: number;
}

// Ornamento de estilo barroco cofrade — inspirado en documentos del s. XVII-XVIII
const BaroqueOrnament = ({ color }: { color: string }) => (
    <svg
        viewBox="0 0 210 297"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute inset-0 w-full h-full pointer-events-none"
        preserveAspectRatio="none"
        fill="none"
        stroke={color}
    >
        {/* Bordes triples */}
        <rect x="4" y="4" width="202" height="289" strokeWidth="1.8"/>
        <rect x="7.5" y="7.5" width="195" height="282" strokeWidth="0.55"/>
        <rect x="11" y="11" width="188" height="275" strokeWidth="0.3"/>

        {/* ═══ ESQUINA SUPERIOR IZQUIERDA ═══ */}
        <path d="M4,42 L4,4 L42,4" strokeWidth="2.4"/>
        {/* Voluta principal */}
        <path d="M4,4 C4,4 32,4 32,22 C32,38 14,40 14,56 C14,64 22,68 30,64" strokeWidth="0.9"/>
        {/* Hoja de acanto izquierda */}
        <path d="M4,16 C12,12 20,10 24,16 C20,20 12,22 10,30 C16,24 24,22 28,30 C24,34 16,36 18,44 C24,38 32,38 34,46" strokeWidth="0.65"/>
        {/* Rizo secundario */}
        <path d="M16,4 C16,4 40,12 36,30 C32,42 18,38 22,52" strokeWidth="0.45"/>
        {/* Medallón floral */}
        <circle cx="22" cy="22" r="4" strokeWidth="0.85"/>
        <circle cx="22" cy="22" r="1.6" fill={color}/>
        <line x1="22" y1="17" x2="22" y2="14" strokeWidth="0.5"/>
        <line x1="27" y1="19" x2="29" y2="17" strokeWidth="0.5"/>
        <line x1="27" y1="25" x2="29" y2="27" strokeWidth="0.5"/>
        <line x1="22" y1="27" x2="22" y2="30" strokeWidth="0.5"/>
        <line x1="17" y1="25" x2="15" y2="27" strokeWidth="0.5"/>
        <line x1="17" y1="19" x2="15" y2="17" strokeWidth="0.5"/>
        {/* Segundo medallón menor */}
        <circle cx="36" cy="36" r="2.8" strokeWidth="0.7"/>

        {/* ═══ ESQUINA SUPERIOR DERECHA ═══ */}
        <path d="M206,42 L206,4 L168,4" strokeWidth="2.4"/>
        <path d="M206,4 C206,4 178,4 178,22 C178,38 196,40 196,56 C196,64 188,68 180,64" strokeWidth="0.9"/>
        <path d="M206,16 C198,12 190,10 186,16 C190,20 198,22 200,30 C194,24 186,22 182,30 C186,34 194,36 192,44 C186,38 178,38 176,46" strokeWidth="0.65"/>
        <path d="M194,4 C194,4 170,12 174,30 C178,42 192,38 188,52" strokeWidth="0.45"/>
        <circle cx="188" cy="22" r="4" strokeWidth="0.85"/>
        <circle cx="188" cy="22" r="1.6" fill={color}/>
        <line x1="188" y1="17" x2="188" y2="14" strokeWidth="0.5"/>
        <line x1="193" y1="19" x2="195" y2="17" strokeWidth="0.5"/>
        <line x1="193" y1="25" x2="195" y2="27" strokeWidth="0.5"/>
        <line x1="188" y1="27" x2="188" y2="30" strokeWidth="0.5"/>
        <line x1="183" y1="25" x2="181" y2="27" strokeWidth="0.5"/>
        <line x1="183" y1="19" x2="181" y2="17" strokeWidth="0.5"/>
        <circle cx="174" cy="36" r="2.8" strokeWidth="0.7"/>

        {/* ═══ ESQUINA INFERIOR IZQUIERDA ═══ */}
        <path d="M4,255 L4,293 L42,293" strokeWidth="2.4"/>
        <path d="M4,293 C4,293 32,293 32,275 C32,259 14,257 14,241 C14,233 22,229 30,233" strokeWidth="0.9"/>
        <path d="M4,281 C12,285 20,287 24,281 C20,277 12,275 10,267 C16,273 24,275 28,267 C24,263 16,261 18,253 C24,259 32,259 34,251" strokeWidth="0.65"/>
        <path d="M16,293 C16,293 40,285 36,267 C32,255 18,259 22,245" strokeWidth="0.45"/>
        <circle cx="22" cy="275" r="4" strokeWidth="0.85"/>
        <circle cx="22" cy="275" r="1.6" fill={color}/>
        <line x1="22" y1="280" x2="22" y2="283" strokeWidth="0.5"/>
        <line x1="27" y1="278" x2="29" y2="280" strokeWidth="0.5"/>
        <line x1="27" y1="272" x2="29" y2="270" strokeWidth="0.5"/>
        <line x1="22" y1="270" x2="22" y2="267" strokeWidth="0.5"/>
        <line x1="17" y1="272" x2="15" y2="270" strokeWidth="0.5"/>
        <line x1="17" y1="278" x2="15" y2="280" strokeWidth="0.5"/>
        <circle cx="36" cy="261" r="2.8" strokeWidth="0.7"/>

        {/* ═══ ESQUINA INFERIOR DERECHA ═══ */}
        <path d="M206,255 L206,293 L168,293" strokeWidth="2.4"/>
        <path d="M206,293 C206,293 178,293 178,275 C178,259 196,257 196,241 C196,233 188,229 180,233" strokeWidth="0.9"/>
        <path d="M206,281 C198,285 190,287 186,281 C190,277 198,275 200,267 C194,273 186,275 182,267 C186,263 194,261 192,253 C186,259 178,259 176,251" strokeWidth="0.65"/>
        <path d="M194,293 C194,293 170,285 174,267 C178,255 192,259 188,245" strokeWidth="0.45"/>
        <circle cx="188" cy="275" r="4" strokeWidth="0.85"/>
        <circle cx="188" cy="275" r="1.6" fill={color}/>
        <line x1="188" y1="280" x2="188" y2="283" strokeWidth="0.5"/>
        <line x1="193" y1="278" x2="195" y2="280" strokeWidth="0.5"/>
        <line x1="193" y1="272" x2="195" y2="270" strokeWidth="0.5"/>
        <line x1="188" y1="270" x2="188" y2="267" strokeWidth="0.5"/>
        <line x1="183" y1="272" x2="181" y2="270" strokeWidth="0.5"/>
        <line x1="183" y1="278" x2="181" y2="280" strokeWidth="0.5"/>
        <circle cx="174" cy="261" r="2.8" strokeWidth="0.7"/>

        {/* ═══ CARTELA SUPERIOR CENTRAL ═══ */}
        <path d="M62,4 Q105,-2 148,4" strokeWidth="0.85"/>
        <path d="M68,7.5 Q105,2 142,7.5" strokeWidth="0.4"/>
        {/* Cruz con gloria */}
        <line x1="105" y1="0" x2="105" y2="12" strokeWidth="1.6"/>
        <line x1="98.5" y1="5.5" x2="111.5" y2="5.5" strokeWidth="1.6"/>
        <circle cx="105" cy="5.5" r="4.5" strokeWidth="0.9"/>
        <circle cx="105" cy="5.5" r="2" fill={color}/>
        {/* Hojas palmeta */}
        <path d="M74,5 C79,1 86,2 89,7 C86,10 79,9 74,5 Z" strokeWidth="0.6"/>
        <path d="M136,5 C141,1 148,2 151,7 C148,10 141,9 136,5 Z" strokeWidth="0.6"/>
        {/* Puntos ornamentales */}
        <circle cx="66" cy="6" r="1.3" fill={color} strokeWidth="0"/>
        <circle cx="144" cy="6" r="1.3" fill={color} strokeWidth="0"/>
        {/* Pequeñas flores */}
        <circle cx="80" cy="6" r="1.5" strokeWidth="0.5"/>
        <circle cx="130" cy="6" r="1.5" strokeWidth="0.5"/>

        {/* ═══ CARTELA INFERIOR CENTRAL ═══ */}
        <path d="M62,293 Q105,299 148,293" strokeWidth="0.85"/>
        <path d="M68,289.5 Q105,295 142,289.5" strokeWidth="0.4"/>
        <line x1="105" y1="293" x2="105" y2="285" strokeWidth="1.6"/>
        <line x1="98.5" y1="291.5" x2="111.5" y2="291.5" strokeWidth="1.6"/>
        <circle cx="105" cy="291.5" r="4.5" strokeWidth="0.9"/>
        <circle cx="105" cy="291.5" r="2" fill={color}/>
        <path d="M74,292 C79,296 86,295 89,290 C86,287 79,288 74,292 Z" strokeWidth="0.6"/>
        <path d="M136,292 C141,296 148,295 151,290 C148,287 141,288 136,292 Z" strokeWidth="0.6"/>
        <circle cx="66" cy="291" r="1.3" fill={color} strokeWidth="0"/>
        <circle cx="144" cy="291" r="1.3" fill={color} strokeWidth="0"/>
        <circle cx="80" cy="291" r="1.5" strokeWidth="0.5"/>
        <circle cx="130" cy="291" r="1.5" strokeWidth="0.5"/>

        {/* ═══ ORNAMENTO LATERAL IZQUIERDO CENTRAL ═══ */}
        <path d="M4,96 C-1,110 -1,128 4,142" strokeWidth="0.8"/>
        <path d="M7.5,104 C3.5,116 3.5,130 7.5,141" strokeWidth="0.4"/>
        {/* Medallón lateral */}
        <circle cx="5.5" cy="148.5" r="5.5" strokeWidth="0.9"/>
        <circle cx="5.5" cy="148.5" r="2.4" fill={color} opacity="0.7"/>
        <line x1="0" y1="148.5" x2="3.5" y2="148.5" strokeWidth="0.75"/>
        <line x1="5.5" y1="143" x2="5.5" y2="145.5" strokeWidth="0.75"/>
        <line x1="5.5" y1="151.5" x2="5.5" y2="154" strokeWidth="0.75"/>
        <line x1="7.5" y1="148.5" x2="11" y2="148.5" strokeWidth="0.75"/>
        {/* Volutas barrocas */}
        <path d="M4,114 C7,111 9,113 8,118 C7,123 5,123 6,128 C7,133 9,133 9,138" strokeWidth="0.55"/>
        <path d="M4,166 C7,169 9,167 8,162 C7,157 5,157 6,152 C7,147 9,147 9,142" strokeWidth="0.55"/>

        {/* ═══ ORNAMENTO LATERAL DERECHO CENTRAL ═══ */}
        <path d="M206,96 C211,110 211,128 206,142" strokeWidth="0.8"/>
        <path d="M202.5,104 C206.5,116 206.5,130 202.5,141" strokeWidth="0.4"/>
        <circle cx="204.5" cy="148.5" r="5.5" strokeWidth="0.9"/>
        <circle cx="204.5" cy="148.5" r="2.4" fill={color} opacity="0.7"/>
        <line x1="210" y1="148.5" x2="206.5" y2="148.5" strokeWidth="0.75"/>
        <line x1="204.5" y1="143" x2="204.5" y2="145.5" strokeWidth="0.75"/>
        <line x1="204.5" y1="151.5" x2="204.5" y2="154" strokeWidth="0.75"/>
        <line x1="202.5" y1="148.5" x2="199" y2="148.5" strokeWidth="0.75"/>
        <path d="M206,114 C203,111 201,113 202,118 C203,123 205,123 204,128 C203,133 201,133 201,138" strokeWidth="0.55"/>
        <path d="M206,166 C203,169 201,167 202,162 C203,157 205,157 204,152 C203,147 201,147 201,142" strokeWidth="0.55"/>

        {/* ═══ ROMBOS EN BORDE SUPERIOR E INFERIOR ═══ */}
        {[48, 62, 76, 134, 148, 162].map((x, i) => (
            <path key={`tr-${i}`} d={`M${x},4 L${x+2.5},7 L${x+5},4 L${x+2.5},1 Z`} strokeWidth="0.45"/>
        ))}
        {[48, 62, 76, 134, 148, 162].map((x, i) => (
            <path key={`br-${i}`} d={`M${x},293 L${x+2.5},290 L${x+5},293 L${x+2.5},296 Z`} strokeWidth="0.45"/>
        ))}

        {/* ═══ MARCAS LATERALES ═══ */}
        {[55, 80, 220, 245].map((y, i) => (
            <g key={`lm-${i}`}>
                <line x1="4" y1={y} x2="8" y2={y} strokeWidth="1.3"/>
                <line x1="202" y1={y} x2="206" y2={y} strokeWidth="1.3"/>
            </g>
        ))}

        {/* ═══ FILIGRANA INTERIOR ═══ */}
        <rect x="15" y="15" width="180" height="267" strokeWidth="0.18" strokeDasharray="3 5"/>
    </svg>
);

function toDateSafe(v: any): Date {
    if (!v) return new Date();
    if (typeof v === 'object' && typeof v.toDate === 'function') return v.toDate();
    const d = new Date(v);
    return isNaN(d.getTime()) ? new Date() : d;
}

/**
 * Renderiza una papeleta YA EMITIDA (documento de Firestore).
 *
 * Úsalo siempre en lugar de montar `PapeletaDeSitioPreview` a mano: así la que
 * ve el hermano en su buzón y la que ve la hermandad son exactamente la misma
 * (mismo ancho, mismos campos y mismo mapeo). Antes el buzón pasaba el nombre
 * de la hermandad como `nombre` y la preview solo leía `nombreCompleto`, con lo
 * que la papeleta del buzón salía sin nombre de hermandad.
 */
export function PapeletaDeSitioDoc({
    papeleta,
    hermano,
    className,
}: {
    papeleta: PapeletaDeSitio;
    hermano: UserProfile | null;
    className?: string;
}) {
    return (
        <div className={cn('w-full max-w-xs', className)}>
            <PapeletaDeSitioPreview
                hermandad={{
                    nombre: papeleta.hermandadNombre,
                    nombreCompleto: papeleta.hermandadNombre,
                    escudoUrl: papeleta.hermandadEscudo,
                    sede: papeleta.hermandadSede,
                    colorPrimario: papeleta.ornamentColor,
                    colorSecundario: papeleta.selloColor,
                }}
                hermano={hermano}
                diaDeSalida={papeleta.diaDeSalida}
                cargo={papeleta.cargo}
                hora={papeleta.hora}
                fechaCreacion={toDateSafe(papeleta.sentAt)}
                ornamentColor={papeleta.ornamentColor}
                titleColor={papeleta.titleColor}
                selloColor={papeleta.selloColor}
                selloOpacity={papeleta.selloOpacity}
            />
        </div>
    );
}

export function PapeletaDeSitioPreview({
    hermandad,
    hermano,
    diaDeSalida,
    cargo,
    hora,
    fechaCreacion,
    ornamentColor,
    titleColor,
    selloColor,
    selloOpacity,
}: PapeletaProps) {
    return (
        <div className="w-full aspect-[1/1.41] bg-[#fdf8ee] text-black font-serif relative overflow-hidden flex flex-col shadow-lg">
            <BaroqueOrnament color={ornamentColor} />

            <div className="relative z-10 flex flex-col items-center justify-start flex-grow h-full text-center px-8 py-6">
                <header className="flex flex-col items-center space-y-1 mb-3">
                    {hermandad.escudoUrl && (
                        <img src={hermandad.escudoUrl} alt="Escudo" className="h-14 w-14 object-contain mb-1 drop-shadow-sm" />
                    )}
                    <p className="text-[0.58rem] font-semibold tracking-[0.12em] uppercase leading-snug max-w-[75%]">
                        {hermandad.nombreCompleto || hermandad.nombre}
                    </p>
                    <div className="flex items-center gap-3 my-1.5">
                        <div className="h-px w-10" style={{ background: ornamentColor, opacity: 0.5 }} />
                        <h1 className="text-[0.65rem] font-bold tracking-[0.22em] uppercase" style={{ color: titleColor }}>
                            Papeleta de Sitio
                        </h1>
                        <div className="h-px w-10" style={{ background: ornamentColor, opacity: 0.5 }} />
                    </div>
                </header>

                <main className="text-[0.58rem] space-y-1.5 leading-relaxed flex-grow flex flex-col items-center justify-center">
                    <p className="opacity-65 italic">Nuestro Hermano D./Dña.</p>
                    <p className="font-semibold text-[0.68rem] border-b border-dotted border-gray-400 min-w-[65%] pb-px min-h-[1rem]">
                        {hermano?.nickname || ' '}
                    </p>
                    <p className="mt-1 opacity-80 max-w-[82%] leading-snug">
                        acompañará a nuestros Sagrados Titulares en su Estación de Penitencia a la Santa Iglesia Metropolitana de Jartiland el{' '}
                        <strong>{diaDeSalida || '[día de salida]'}</strong> de este año 2026
                    </p>
                    <p className="mt-1.5 opacity-65 italic">ocupando el cargo de:</p>
                    <p className="font-semibold text-[0.68rem] border-b border-dotted border-gray-400 min-w-[55%] pb-px min-h-[1rem]">
                        {cargo || ' '}
                    </p>
                    <p className="mt-2 opacity-80 leading-snug max-w-[82%]">
                        Deberá estar presente en el lugar de salida
                    </p>
                    <p className="font-semibold text-[0.62rem] max-w-[70%]">{hermandad.sede}</p>
                    <p className="opacity-80">
                        a las <strong>{hora || '[hora]'}</strong> del día{' '}
                        <strong>{diaDeSalida || '[día de salida]'}</strong>
                    </p>
                </main>

                <footer className="text-[0.55rem] mt-2 opacity-60 italic">
                    En Jartiland a {format(fechaCreacion, "d 'de' MMMM 'de' yyyy", { locale: es })}
                </footer>

                {selloColor && hermandad.escudoUrl && (
                    <div className="absolute bottom-6 right-6 h-16 w-16 transform rotate-[-12deg]">
                        <img
                            src={hermandad.escudoUrl}
                            alt="Sello"
                            className="h-full w-full object-contain"
                            style={{
                                opacity: selloOpacity ?? 0.25,
                                filter: 'grayscale(100%) sepia(100%) saturate(400%)',
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
